/**
 * Bundle 51 - RSVP auth gate modal.
 *
 * Opens when an anonymous user clicks "RSVP". Shows the same three
 * auth options as `/login` but embedded so we do not lose the RSVP
 * intent. Google + Magic-link are redirect flows (Emergent-managed
 * Google auth is a hosted redirect, not an in-page JS SDK), so we
 * pass a `redirect` URL that carries the user back to this event
 * page with `?rsvp=1` so the RSVP flow reopens automatically after
 * sign-in. Email+password submits inline and closes the modal on
 * success.
 *
 * Design decision note (Bundle 51 spec vs codebase reality):
 * user approved the raw Google JS SDK pattern, but this codebase
 * uses Emergent-managed Google Auth which is a full-page redirect
 * flow (not modal-embedded). We use the site's canonical redirect
 * pattern and mirror the LoginPage UX. The `redirect=` param
 * preserves the RSVP intent so the modal reopens after sign-in.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const GoogleGlyph = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const AuthGateModal = ({ open, onClose, rsvpRedirectPath, title, description }) => {
  const { login } = useAuth();
  const [mode, setMode] = useState('choices');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const redirectUrl = (() => {
    const base = window.location.origin + (rsvpRedirectPath || window.location.pathname);
    const u = new URL(base);
    u.searchParams.set('rsvp', '1');
    return u.toString();
  })();

  const goGoogle = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendMagicLink = async () => {
    if (!email) { setErr('Enter your email.'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/auth/magic-link', { email, redirect: redirectUrl });
      toast.success('Magic link sent. Check your inbox.');
      setMode('magic-sent');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not send magic link.');
    } finally { setBusy(false); }
  };

  const emailPasswordLogin = async () => {
    setBusy(true); setErr('');
    try {
      await login(email, password);
      toast.success('Signed in.');
      onClose(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sign in failed.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(false); }}>
      <DialogContent className="max-w-md" data-testid="auth-gate-modal">
        <DialogHeader>
          <DialogTitle>{title || 'Sign in to RSVP'}</DialogTitle>
          <DialogDescription>
            {description || 'Takes 60 seconds. We use your account so organizers can welcome you.'}
          </DialogDescription>
        </DialogHeader>

        {err && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-900 flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {mode === 'choices' && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={goGoogle}
              className="w-full flex items-center justify-center gap-3 rounded-xl border border-secondary-300 bg-gradient-to-r from-white to-secondary-50 px-4 py-3 text-primary-900 font-semibold text-sm shadow-sm hover:shadow-gold transition-all"
              data-testid="auth-gate-google"
            >
              <GoogleGlyph className="h-5 w-5" />
              <span>Continue with Google</span>
            </button>
            <button
              type="button"
              onClick={() => setMode('magic')}
              className="w-full text-sm font-semibold border border-neutral-300 rounded-xl px-4 py-3 hover:bg-neutral-50 flex items-center justify-center gap-2"
              data-testid="auth-gate-magic-link-btn"
            >
              <Mail className="h-4 w-4" /> Email me a magic link
            </button>
            <button
              type="button"
              onClick={() => setMode('password')}
              className="w-full text-xs text-neutral-600 hover:text-primary-800 underline"
              data-testid="auth-gate-password-btn"
            >
              Use email and password instead
            </button>
          </div>
        )}

        {mode === 'magic' && (
          <div className="space-y-3">
            <Label htmlFor="magic-email">Your email</Label>
            <Input
              id="magic-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              data-testid="auth-gate-email-input"
            />
            <Button
              type="button"
              onClick={sendMagicLink}
              disabled={busy || !email}
              className="w-full"
              data-testid="auth-gate-send-magic"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending...</> : 'Send magic link'}
            </Button>
            <button type="button" onClick={() => setMode('choices')} className="w-full text-xs text-neutral-500 underline">
              Back
            </button>
          </div>
        )}

        {mode === 'magic-sent' && (
          <div className="text-sm text-primary-900 rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="font-semibold mb-1">Check {email}</div>
            The link signs you in and drops you back on this event, ready to RSVP.
          </div>
        )}

        {mode === 'password' && (
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); emailPasswordLogin(); }}
          >
            <div>
              <Label htmlFor="pw-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <Input
                  id="pw-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  autoComplete="email"
                  data-testid="auth-gate-pw-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pw-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <Input
                  id="pw-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  autoComplete="current-password"
                  data-testid="auth-gate-pw-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy || !email || !password}
              className="w-full"
              data-testid="auth-gate-pw-submit"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Signing in...</> : 'Sign in'}
            </Button>
            <button type="button" onClick={() => setMode('choices')} className="w-full text-xs text-neutral-500 underline">
              Back
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthGateModal;
